import { useCallback, useEffect, useState } from "react";
import PageContainer from "../components/pageContainer/PageContainer";
import SettingsLayout from "../components/settings/SettingsLayout";
import SettingsPageHeader from "../components/settings/SettingsPageHeader";
import SettingsEntityRow from "../components/settings/SettingsEntityRow";
import Button from "../components/button";
import useModal from "../components/modal/useModal";
import UseContext from "../hooks/useContext";
import { ChevronDown, Pencil, Plus } from "../assets/icons";
import useTitle from "../hooks/TitleManager";
import useIntegrations from "../hooks/useIntegrations";
import ApiDatabase from "../server";
import { SettingsQualificationsListGhost } from "../components/ghost/SettingsAndEmployeesGhost";
import SettingsHelpBanner from "../components/settings/SettingsHelpBanner";
import EmptyState from "../components/EmptyState/EmptyState";

/** En-tête accordéon : titres sans cadre, chevron en Button SECONDARY (aligné Secteurs). */
const AccordionSectionHeader = ({ title, description, isOpen, onToggle }) => (
  <div className="flex items-start justify-between gap-3 px-4">
    <button
      type="button"
      className="min-w-0 flex-1 rounded-md text-left outline-none ring-gray-200 focus-visible:ring-2"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {description ? (
        <p className="mt-1 text-[14px] leading-snug text-gray-500">{description}</p>
      ) : null}
    </button>
    <Button
      color="SECONDARY"
      size="ICON"
      onClick={onToggle}
      title="Replie ou déplie la section"
    >
      <span
        className={`inline-flex transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
      >
        <ChevronDown wh={20} color="#374151" />
      </span>
    </Button>
  </div>
);

const QualificationRow = ({ qualification, readOnly, onEdit }) => (
  <SettingsEntityRow
    label={qualification.name}
    actionsSlot={
      !readOnly ? (
        <Button color="ICON" size="ICON" onClick={() => onEdit(qualification)}>
          <Pencil wh={20} color="#374151" />
        </Button>
      ) : null
    }
  />
);

const SettingsQualifications = () => {
  useTitle({ title: "Paramètres - Postes" });

  const [qualifications, setQualifications] = useState({
    workfully: [],
    peopulse: [],
    jobbiz: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [accordionOpen, setAccordionOpen] = useState({
    workfully: true,
    peopulse: true,
    jobbiz: true,
  });
  const { toggle } = useModal();
  const { setModalData } = UseContext();
  const integrations = useIntegrations();

  const refresh = useCallback(() => {
    setIsLoading(true);
    ApiDatabase.getQualifications(
      {},
      (data) => {
        setQualifications(
          data?.grouped || { workfully: [], peopulse: [], jobbiz: [] }
        );
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpenCreate = () => {
    setModalData({
      onSuccess: () => {
        refresh();
      },
    });
    toggle("create_qualification_simple");
  };

  const handleOpenEdit = (qualification) => {
    setModalData({
      qualification,
      onSuccess: () => {
        refresh();
      },
      onDeleteRequest: (q) => {
        // Pass deletion request back to delete modal
        setTimeout(() => {
          setModalData({
            qualification: q,
            onSuccess: () => {
              refresh();
            },
          });
          toggle("delete_qualification");
        }, 0);
      },
    });
    toggle("edit_qualification");
  };

  const integrationLabel = (source) =>
    source === "peopulse" ? "Peopulse" : source === "jobbiz" ? "Jobbiz" : "Workfully";

  const integrationRecuperesLine = (count, source) => {
    const brand = source === "peopulse" ? "Peopulse" : "Jobbiz";
    if (count === 0) return `0 récupéré depuis ${brand}`;
    if (count === 1) return `1 récupérée depuis ${brand}`;
    return `${count} récupérées depuis ${brand}`;
  };

  const workfullyCreesLine = (count) =>
    count <= 1 ? `${count} créé depuis Workfully` : `${count} créés depuis Workfully`;

  return (
    <PageContainer type="settings_qualifications">
      <div className="flex w-full max-w-[1122px] flex-col gap-6">
        <div className="grid grid-cols-1 gap-8 pt-6 lg:grid-cols-[minmax(0,1fr)_minmax(400px,440px)] lg:items-stretch">
          <SettingsLayout withTopPadding={false} innerScroll={false}>
            <div className="flex flex-col gap-6">
              <SettingsPageHeader
                title="Postes"
                action={
                  <Button
                    color="PRIMARY"
                    size="SM"
                    onClick={handleOpenCreate}
                  >
                    <div className="flex items-center gap-1">
                      <Plus wh={20} color="#FFFFFF" />
                      Créer
                    </div>
                  </Button>
                }
              />

              {/* {integrations.hasAny && (
                <Banner type="info" hideIcon>
                  <div className="flex flex-col gap-1">
                    <div className="font-semibold text-[#111827]">
                      Intégrations
                    </div>
                    <div>
                      Les postes sont automatiquement importés depuis vos
                      intégrations. Vous pouvez tout de même créer des postes
                      manuels.
                    </div>
                  </div>
                </Banner>
              )} */}

              {isLoading ? (
                <section className="flex flex-col pt-6">
                  <SettingsQualificationsListGhost />
                </section>
              ) : (qualifications.workfully || []).length === 0 ? (
                <div className="flex flex-col pt-6">
                  <EmptyState type="settings_postes" />
                </div>
              ) : (
                <section className="flex flex-col gap-3 pt-6">
                  <AccordionSectionHeader
                    title="Workfully"
                    description={workfullyCreesLine(
                      (qualifications.workfully || []).length
                    )}
                    isOpen={accordionOpen.workfully}
                    onToggle={() =>
                      setAccordionOpen((prev) => ({
                        ...prev,
                        workfully: !prev.workfully,
                      }))
                    }
                  />
                  {accordionOpen.workfully && (
                    <div className="flex max-h-[260px] flex-col gap-0 overflow-y-auto">
                      {qualifications.workfully.map((qualification) => (
                        <QualificationRow
                          key={qualification._id}
                          qualification={qualification}
                          onEdit={handleOpenEdit}
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}

              {["peopulse", "jobbiz"].map((source) => {
                if (!integrations[source]) return null;
                const list = qualifications[source] || [];
                if (list.length === 0) return null;
                const isOpen = accordionOpen[source];
                return (
                  <section key={source} className="flex flex-col gap-3 pt-6">
                    <AccordionSectionHeader
                      title={integrationLabel(source)}
                      description={integrationRecuperesLine(list.length, source)}
                      isOpen={isOpen}
                      onToggle={() =>
                        setAccordionOpen((prev) => ({
                          ...prev,
                          [source]: !prev[source],
                        }))
                      }
                    />
                    {isOpen && (
                      <div className="flex max-h-[260px] flex-col gap-0 overflow-y-auto">
                        {list.map((qualification) => (
                          <QualificationRow
                            key={qualification._id}
                            qualification={qualification}
                            readOnly
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </SettingsLayout>

          <aside className="hidden min-w-0 lg:block">
            <div>
              <SettingsHelpBanner
                title="Qu'est-ce qu'un poste ?"
                description="Les postes vous permettent de catégoriser vos shifts par fonction (chef de chantier, manutentionnaire, …)."
              />
            </div>
          </aside>
        </div>
      </div>
    </PageContainer>
  );
};

export default SettingsQualifications;
